import "core-js/modules/es.array.push.js";
import { DENIAL_ROLE, HISTORY_MAX_MESSAGES, ROLE_ASSISTANT, ROLE_USER, appendMessage, clearHistory, getOpenedStatus, saveOpenedStatus, loadHistory, saveHistory, STORAGE_KEY } from './chatbot-history';
import { sendChatbotMessage } from './chatbot-api';
import { isMobile } from '@/utils';
import { escapeHtml } from '@/utils/html';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import ChatbotIcon from '@/dow-platform/components/commerce/blocks/svg-elements/chatbot-icon/chatbotIcon.vue';
const REASON_TAMPERED = 'history_tampered';
const CHATBOT_TAGS_FROM_TEMPLATES = ['a', 'br', 'p'];
const CHATBOT_TAGS_FROM_MODEL_PROSE = ['code', 'em', 'li', 'ol', 'strong', 'ul'];
const CHATBOT_ALLOWED_TAGS = [...CHATBOT_TAGS_FROM_TEMPLATES, ...CHATBOT_TAGS_FROM_MODEL_PROSE];
const CHATBOT_ALLOWED_ATTR = ['href', 'title'];
const CHATBOT_ALLOWED_URI = /^(?:https?:\/\/|\/(?!\/)|#)/i;
const chatbotPurifier = DOMPurify(window);
chatbotPurifier.addHook('afterSanitizeAttributes', node => {
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});
export default {
  name: 'Chatbot',
  components: {
    ChatbotIcon
  },
  data() {
    return {
      messages: [],
      historyIndex: -1,
      draft: '',
      isSending: false,
      isOpen: false,
      chatbotOpenClass: 'cmp-chatbot--open',
      announcement: '',
      lastUpdatedTargetId: null
    };
  },
  computed: {
    isFlagEnabled() {
      return window.featureFlags?.chatbotEnabled === true || window.featureFlags?.chatbotEnabled === 'true';
    },
    isLoggedIn() {
      return !!this.$store?.state?.user?.dccUserInfo?.data?.email;
    },
    isChatbotUser() {
      return this.$store?.state?.user?.dccUserInfo?.data?.baseRole?.includes('viewchatbot') ?? false;
    },
    isMobile() {
      return isMobile();
    },
    isEuBlockFlagEnabled() {
      return window.featureFlags?.chatbotEuBlockEnabled === true || window.featureFlags?.chatbotEuBlockEnabled === 'true';
    },
    isEuBlocked() {
      if (!this.isEuBlockFlagEnabled) return false;
      return this.$store?.state?.browser?.euBlocked === true;
    },
    isGeoSettled() {
      if (!this.isEuBlockFlagEnabled) return true;
      return this.$store?.state?.browser?.euBlockResolved === true;
    },
    showChatbot() {
      return this.isFlagEnabled && this.isLoggedIn && this.isChatbotUser && this.isGeoSettled && !this.isEuBlocked;
    },
    userMessageHistory() {
      return this.messages?.filter(msg => msg.role === ROLE_USER).map(msg => msg.text);
    }
  },
  mounted() {
    this.messages = loadHistory();
    this.isOpen = getOpenedStatus() && !this.isMobile;
    this.$nextTick(this.scrollToBottom);
    const globalHeader = document.querySelector('.header');
    if (globalHeader) {
      new IntersectionObserver(entries => {
        Array.prototype.slice.call(entries).forEach(entry => {
          if (!this.$refs.launcher) {
            return;
          }
          if (entry.intersectionRatio <= 0) {
            this.$refs.launcher.classList.add('offset');
          } else {
            this.$refs.launcher.classList.remove('offset');
          }
        });
      }).observe(globalHeader);
    }
    window.addEventListener('storage', e => {
      if (e.key === STORAGE_KEY) {
        this.messages = loadHistory();
        this.$nextTick(this.scrollToBottom);
      }
    });
    document.addEventListener('click', this.onDocumentClick, true);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onDocumentClick, true);
  },
  methods: {
    onDocumentClick(event) {
      if (!this.isOpen) {
        return;
      }
      const target = event.target;
      if (this.$refs.panel?.contains(target)) {
        return;
      }
      if (this.$refs.launcher?.contains(target)) {
        return;
      }
      this.close();
    },
    open() {
      this.isOpen = true;
      saveOpenedStatus(true);
      document.querySelector('html').classList.add(this.chatbotOpenClass);
      this.$nextTick(() => {
        this.scrollToBottom();
        if (this.$refs.chatInput) {
          this.$refs.chatInput.focus();
        }
      });
    },
    close(targetId = null) {
      this.isOpen = false;
      document.querySelector('html').classList.remove(this.chatbotOpenClass);
      saveOpenedStatus(false);
      const focusTargetId = targetId || this.lastUpdatedTargetId;
      this.$nextTick(() => {
        let elementToFocus = null;
        if (focusTargetId) {
          elementToFocus = document.getElementById(focusTargetId);
        }
        if (elementToFocus) {
          if (!elementToFocus.hasAttribute('tabindex')) {
            elementToFocus.setAttribute('tabindex', '-1');
          }
          elementToFocus.focus();
          this.lastUpdatedTargetId = null;
        } else if (this.$refs.launcher) {
          this.$refs.launcher.focus();
        }
      });
    },
    bubbleClass(msg) {
      if (msg.role === ROLE_USER) return 'cmp-chatbot__bubble--user';
      return 'cmp-chatbot__bubble--assistant';
    },
    async onSubmit() {
      const text = this.draft.trim();
      if (!text || this.isSending) return;
      this.historyIndex = -1;
      const history = this.serverHistory();
      this.messages = appendMessage(this.messages, ROLE_USER, text);
      saveHistory(this.messages);
      this.draft = '';
      this.isSending = true;
      this.$nextTick(this.scrollToBottom);
      try {
        let result = await sendChatbotMessage({
          history,
          message: text
        });
        if (result?.denied && result.reason === REASON_TAMPERED) {
          clearHistory();
          this.messages = appendMessage([], ROLE_USER, text);
          result = await sendChatbotMessage({
            history: [],
            message: text
          });
        }
        if (result?.targetId) {
          this.lastUpdatedTargetId = result.targetId;
        }
        this.messages = this.appendReply(this.messages, result);
        if (result?.navigateTo) {
          this.navigate(result.navigateTo);
        }
      } catch (e) {
        this.messages = appendMessage(this.messages, DENIAL_ROLE, this.getLabel('deliveryDetails.page.trackDelivery.error'));
      } finally {
        saveHistory(this.messages);
        this.isSending = false;
        this.$nextTick(this.scrollToBottom);
      }
    },
    appendReply(messages, result) {
      if (result?.denied) {
        return appendMessage(messages, DENIAL_ROLE, this.getLabel('chatbot.error.denied', [result.reason || this.getLabel('deliveryDetails.page.trackDelivery.error')]));
      }
      if (!result?.signature) {
        return appendMessage(messages, DENIAL_ROLE, this.getLabel('deliveryDetails.page.trackDelivery.error'));
      }
      return appendMessage(messages, ROLE_ASSISTANT, result.reply || '', result.signature);
    },
    navigate(target) {
      let url;
      try {
        url = new URL(target, window.location.origin);
      } catch (e) {
        return;
      }
      const current = new URL(window.location.href);
      if (url.href === current.href) {
        return;
      }
      const sameDocument = url.origin === current.origin && url.pathname === current.pathname && url.search === current.search;
      const performNavigation = () => {
        const hashRouted = this.$router?.options?.history?.base?.includes('#');
        if (sameDocument && hashRouted && url.hash.startsWith('#/')) {
          this.$router.push(url.hash.slice(1)).catch(() => window.location.assign(url.href));
          return;
        }
        window.location.assign(url.href);
      };
      if (!sameDocument) {
        this.announcement = this.getLabel('chatbot.redirect.announcement');
        setTimeout(() => {
          performNavigation();
        }, 2000);
      } else {
        performNavigation();
      }
    },
    serverHistory() {
      return this.messages.filter(m => m.role === ROLE_USER || m.role === ROLE_ASSISTANT).slice(-HISTORY_MAX_MESSAGES);
    },
    scrollToBottom() {
      const el = this.$refs.messageList;
      if (el) el.scrollTop = el.scrollHeight;
    },
    formatMessaage(text) {
      const source = text ?? '';
      try {
        return chatbotPurifier.sanitize(marked(source.replace(/\n/g, '<br/>')), {
          ALLOWED_TAGS: CHATBOT_ALLOWED_TAGS,
          ALLOWED_ATTR: CHATBOT_ALLOWED_ATTR,
          ALLOWED_URI_REGEXP: CHATBOT_ALLOWED_URI
        });
      } catch (error) {
        console.error('Chatbot markdown rendering error:', error);
        return escapeHtml(source);
      }
    },
    getLabel(key) {
      return window.Granite.I18n.get(key);
    },
    trapFocus(evt) {
      if (evt.key !== 'Tab') return;
      const focusableElements = this.$refs.panel.querySelectorAll('button:not([disabled]), [href], inpu:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])');
      if (focusableElements.length === 0) {
        evt.preventDefault();
        return;
      }
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (evt.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement.focus();
          evt.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement.focus();
          evt.preventDefault();
        }
      }
    },
    jumpToContent(targetId) {
      this.close(targetId);
    },
    focusInput() {
      if (this.$refs.chatInput) {
        this.$refs.chatInput.focus();
      }
    },
    navigateHistory(direction, event) {
      const inputElement = event.target;
      const currentLength = this.draft.length;
      if (currentLength > 0 && inputElement.selectionStart !== 0 && inputElement.selectionEnd === currentLength) {
        return;
      }
      if (this.userMessageHistory.length === 0) return;
      event.preventDefault();
      if (direction === 'up') {
        if (this.historyIndex < this.userMessageHistory.length - 1) {
          this.historyIndex++;
          const targetIndex = this.userMessageHistory.length - 1 - this.historyIndex;
          this.draft = this.userMessageHistory[targetIndex];
        }
      } else if (direction === 'down') {
        if (this.historyIndex > 0) {
          this.historyIndex--;
          const targetIndex = this.userMessageHistory.length - 1 - this.historyIndex;
          this.draft = this.userMessageHistory[targetIndex];
        } else if (this.historyIndex === 0) {
          this.historyIndex = -1;
          this.draft = '';
        }
      }
    },
    getSpeakerLabel(role) {
      return role === ROLE_USER ? this.getLabel('chatbot.you.said') : this.getLabel('chatbot.agent.said');
    }
  }
};